type SparkPoint = number | null

export interface SparklineProps {
  readonly data: readonly SparkPoint[]
  readonly width?: number
  readonly height?: number
  readonly stroke?: string
  readonly fill?: string
  readonly showFill?: boolean
}

function segmentPath(data: readonly SparkPoint[], width: number, height: number) {
  const nonNull = data.filter((value): value is number => value !== null)
  if (nonNull.length === 0) return { line: [] as string[], yMin: 0, yMax: 1 }
  const yMin = Math.min(...nonNull)
  const yMax = Math.max(...nonNull)
  const range = yMax - yMin || 1
  const step = data.length > 1 ? width / (data.length - 1) : 0
  const yScale = (value: number) => height - 2 - ((value - yMin) / range) * (height - 4)
  const paths: string[] = []
  let current: string[] = []
  data.forEach((point, index) => {
    if (point === null) {
      if (current.length > 0) {
        paths.push(current.join(''))
        current = []
      }
      return
    }
    const command = current.length === 0 ? 'M' : 'L'
    current.push(`${command}${index * step},${yScale(point)}`)
  })
  if (current.length > 0) paths.push(current.join(''))
  return { line: paths, yMin, yMax }
}

export function Sparkline({
  data,
  width = 80,
  height = 24,
  stroke = 'var(--brand)',
  fill = 'var(--brand-soft)',
  showFill = true,
}: SparklineProps) {
  if (data.length === 0) return null
  const { line } = segmentPath(data, width, height)
  if (line.length === 0) return null
  const base = `L${width},${height}L0,${height}Z`
  return (
    <svg width={width} height={height} style={{ display: 'block' }} aria-hidden="true">
      {showFill
        ? line.map((segment, index) => (
            <path key={index} data-part="fill" d={`${segment}${base}`} fill={fill} opacity={0.5} />
          ))
        : null}
      {line.map((segment, index) => (
        <path key={index} data-part="line" d={segment} stroke={stroke} fill="none" strokeWidth={1.5} strokeLinejoin="round" strokeLinecap="round" />
      ))}
    </svg>
  )
}
